'use client';
import React from 'react';
import { AccountRecord } from '@/data/mockAccounts';
import StatusBadge from './StatusBadge';
import CopyButton from './CopyButton';
import RelativeTime from './RelativeTime';
import FlagIcon from './FlagIcon';

interface ListingCardProps {
  record: AccountRecord;
}

export default function ListingCard({ record }: ListingCardProps) {
  const isUnavailable = record.status === 'unavailable';

  return (
    <article
      className={`bg-white border rounded-xl p-4 card-hover transition-all ${
        isUnavailable ? 'opacity-60 border-border' : 'border-border hover:border-primary/30'
      }`}
      aria-label={`Apple ID: ${record.maskedAccount}, ${record.region}, trạng thái ${record.status}`}
    >
      {/* Desktop: horizontal layout */}
      <div className="hidden sm:flex items-center gap-4">
        {/* Account + Region */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-semibold text-foreground truncate">
              {record.maskedAccount}
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-muted bg-soft px-2 py-0.5 rounded-pill border border-border flex-shrink-0">
              <FlagIcon countryCode={record.regionCode} size={14} /> {record.region}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <StatusBadge status={record.status} size="sm" />
            <span className="text-[11px] text-muted flex items-center gap-1">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              <RelativeTime dateStr={record.checkedAt} />
            </span>
            <span className="text-[11px] text-muted/70 hidden lg:inline">{record.sourceLabel}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <CopyButton
            value={record.mockAccountValue}
            label="Tài khoản"
            variant="secondary"
            disabled={isUnavailable}
            region={record.region}
            className="text-xs px-3 py-2 min-h-[40px]"
          />
          <CopyButton
            value={record.mockPasswordValue}
            label="Mật khẩu"
            variant="primary"
            disabled={isUnavailable}
            region={record.region}
            className="text-xs px-3 py-2 min-h-[40px]"
          />
        </div>
      </div>

      {/* Mobile: stacked layout */}
      <div className="sm:hidden">
        {/* Row 1: account + region */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="font-mono text-[13px] font-semibold text-foreground truncate flex-1">
            {record.maskedAccount}
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs text-muted bg-soft px-2 py-0.5 rounded-pill border border-border flex-shrink-0">
            <FlagIcon countryCode={record.regionCode} size={14} /> {record.regionCode}
          </span>
        </div>

        {/* Row 2: status + time */}
        <div className="flex items-center gap-2 mb-3">
          <StatusBadge status={record.status} size="sm" />
          <span className="text-[11px] text-muted flex items-center gap-1">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            <RelativeTime dateStr={record.checkedAt} />
          </span>
        </div>

        {/* Row 3: actions */}
        <div className="listing-card-actions">
          <CopyButton
            value={record.mockAccountValue}
            label="Tài khoản"
            variant="secondary"
            disabled={isUnavailable}
            region={record.region}
            className="w-full text-[13px]"
          />
          <CopyButton
            value={record.mockPasswordValue}
            label="Mật khẩu"
            variant="primary"
            disabled={isUnavailable}
            region={record.region}
            className="w-full text-[13px]"
          />
        </div>
      </div>
    </article>
  );
}